import {memo, useMemo} from 'react'
import {cn} from '@/shared/lib'
import {MAX_THRESHOLD} from './logic/types'

interface AudioLevelMeterProps {
  level: number
  threshold: number
  autoThreshold?: number
  isManual: boolean
  className?: string
}

/**
 * Horizontal audio level meter with a threshold indicator line.
 *
 * - Green fill shows the current mic level
 * - In manual mode: amber line shows user-set threshold
 * - In automatic mode: cyan dashed line shows the adaptive threshold
 * - Fill dims when level is below the active threshold
 */
export const AudioLevelMeter = memo(function AudioLevelMeter({
  level,
  threshold,
  autoThreshold = 0,
  isManual,
  className,
}: AudioLevelMeterProps) {
  const levelPercent = useMemo(() => Math.min((level / MAX_THRESHOLD) * 100, 100), [level])

  const activeThreshold = isManual ? threshold : autoThreshold

  const thresholdPercent = useMemo(
    () => Math.min((activeThreshold / MAX_THRESHOLD) * 100, 100),
    [activeThreshold],
  )

  const isAboveThreshold = level >= activeThreshold

  return (
    <div
      className={cn('relative h-2 w-full rounded-full bg-foreground/15 overflow-hidden', className)}
    >
      <div
        className={cn(
          'absolute inset-y-0 left-0 rounded-full transition-colors duration-75',
          isAboveThreshold ? 'bg-signal-success' : 'bg-signal-success/30',
        )}
        style={{width: `${levelPercent}%`}}
      />

      {isManual && (
        <div
          className="absolute inset-y-0 w-0.5 bg-signal-warning/80 z-10"
          style={{left: `${thresholdPercent}%`}}
        />
      )}

      {!isManual && (
        <div
          className="absolute inset-y-0 w-0.5 z-10"
          style={{
            left: `${thresholdPercent}%`,
            backgroundImage:
              'repeating-linear-gradient(to bottom, color-mix(in oklch, var(--signal-info) 80%, transparent) 0px, color-mix(in oklch, var(--signal-info) 80%, transparent) 2px, transparent 2px, transparent 4px)',
          }}
        />
      )}
    </div>
  )
})
